import { Check } from '@mui/icons-material';
import {
  Box,
  FormControlLabel,
  Switch,
  Typography,
  useTheme
} from '@mui/material';
import { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { Invoice, PaymentChannel } from 'openapi';

import { useTranslations } from 'context/TranslationContext';

import { PAYMENT_CHANNELS_TRANSLATION_KEYS } from 'utils/constants/invoices';
import { InvoiceVerificationFieldName } from 'utils/constants/invoiceVerificationFieldNames';
import { getInvoiceHasConfidenceData } from 'utils/helpers/confidenceHelpers';
import { getDocumentPaymentStatusSummary } from 'utils/helpers/invoiceSectionSummaryHelpers';

import * as DocumentPaymentStatusSectionStyles from 'styles/components/DocumentPaymentStatusSectionStyle';

import { InvoiceVerificationCard } from './InvoiceVerificationCard';

export const DocumentPaymentStatusSection = ({
  currentInvoice,
  onExpanded
}: {
  currentInvoice: Invoice;
  onExpanded?: () => void;
}) => {
  const theme = useTheme();
  const { translate } = useTranslations();
  const { control, setValue } = useFormContext();

  const hasConfidenceData = getInvoiceHasConfidenceData(currentInvoice);

  const isPrepaid = useWatch({ name: InvoiceVerificationFieldName.IsPrepaid });
  const paymentChannel = useWatch({
    name: InvoiceVerificationFieldName.PaymentChannel
  });

  const summary = useMemo(
    () =>
      getDocumentPaymentStatusSummary({ isPrepaid, paymentChannel }, translate),
    [isPrepaid, paymentChannel, translate]
  );

  const handleToggle = (checked: boolean) => {
    setValue(InvoiceVerificationFieldName.IsPrepaid, checked, {
      shouldDirty: true
    });
    if (checked && !paymentChannel) {
      setValue(
        InvoiceVerificationFieldName.PaymentChannel,
        PaymentChannel.BANK,
        { shouldDirty: true }
      );
    }
  };

  const checkmarkAdornment = (
    <Box
      data-testid="document-payment-status-checkmark"
      sx={DocumentPaymentStatusSectionStyles.checkmarkContainer(
        theme.palette.success.main
      )}
    >
      <Check
        sx={DocumentPaymentStatusSectionStyles.checkmarkIcon(
          theme.palette.success.main
        )}
      />
    </Box>
  );

  return (
    <InvoiceVerificationCard
      title={translate('labels.documentPaymentStatus')}
      summary={summary}
      headerStartAdornment={hasConfidenceData ? checkmarkAdornment : undefined}
      onExpanded={onExpanded}
      defaultExpanded={false}
    >
      <Controller
        name={InvoiceVerificationFieldName.IsPrepaid}
        control={control}
        render={({ field }) => (
          <Box sx={DocumentPaymentStatusSectionStyles.toggleWrapper(isPrepaid)}>
            <FormControlLabel
              sx={DocumentPaymentStatusSectionStyles.toggleLabel(isPrepaid)}
              control={
                <Switch
                  {...field}
                  checked={field.value}
                  onChange={(event) => handleToggle(event.target.checked)}
                  sx={DocumentPaymentStatusSectionStyles.switchStyle}
                />
              }
              label={translate('labels.alreadyPaid')}
            />
          </Box>
        )}
      />

      {isPrepaid && (
        <Controller
          name={InvoiceVerificationFieldName.PaymentChannel}
          control={control}
          render={({ field }) => {
            const selectedChannel = field.value || PaymentChannel.BANK;

            return (
              <Box>
                <Typography
                  sx={DocumentPaymentStatusSectionStyles.paymentChannelLabel}
                >
                  {translate('labels.paymentChannel')}
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {Object.values(PaymentChannel).map((channel) => {
                    const isSelected = channel === selectedChannel;

                    return (
                      <Box
                        key={channel}
                        data-testid={`payment-channel-option-${channel}`}
                        data-selected={isSelected}
                        onClick={() => field.onChange(channel)}
                        sx={DocumentPaymentStatusSectionStyles.paymentChannelOption(
                          isSelected
                        )}
                      >
                        {translate(PAYMENT_CHANNELS_TRANSLATION_KEYS[channel])}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          }}
        />
      )}
    </InvoiceVerificationCard>
  );
};
